import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  CalendarRange,
  Layers,
  MapPin,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  TrendingUp,
  UserRound,
} from 'lucide-react';
import { sessionProgramsApi } from '../../services/api/session-programs.api';
import { authApi, type AdminAuthUser } from '../../services/api/auth.api';
import { canPerformAdminAction, canSeeAdminSection } from '../../utils/adminPermissions';
import { LanguageFlag } from '../../components/admin/LanguageFlag';
import { SESSION_LANGUAGE_OPTIONS, timezoneShortLabel } from '../../components/admin/sessionFormOptions';
import type { SessionProgram, SessionProgramSlot } from '@business-game/shared';

type ProgramInsights = {
  count: number;
  upcoming: number;
  past: number;
  nextDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  spanMonths: number;
  perMonthAvg: number;
  yearLabel: string | null;
  languages: NonNullable<SessionProgramSlot['language']>[];
  timezone: string | null;
  cities: string[];
  facilitators: string[];
  sessionCodes: string[];
};

const getProgramInsights = (slots: SessionProgramSlot[]): ProgramInsights => {
  const now = Date.now();
  const dated = slots
    .filter((s) => s.scheduledAt)
    .map((s) => ({ date: new Date(s.scheduledAt), tz: s.timezone, code: s.sessionCode }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  let yearLabel: string | null = null;

  dated.forEach(({ date }) => {
    if (!yearLabel) yearLabel = String(date.getFullYear());
    else if (yearLabel !== String(date.getFullYear())) yearLabel = `${yearLabel}–${date.getFullYear()}`;
  });

  const upcomingDates = dated.filter(({ date }) => date.getTime() >= now);
  const startDate = dated[0]?.date ?? null;
  const endDate = dated[dated.length - 1]?.date ?? null;

  let spanMonths = 0;
  if (startDate && endDate) {
    spanMonths =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth()) +
      1;
  }

  const langs = new Set(slots.map((s) => s.language).filter(Boolean));
  const cities = Array.from(new Set(slots.map((s) => s.location?.trim()).filter(Boolean))) as string[];
  const facilitators = Array.from(new Set(slots.map((s) => s.facilitator?.trim()).filter(Boolean))) as string[];

  return {
    count: dated.length,
    upcoming: upcomingDates.length,
    past: dated.length - upcomingDates.length,
    nextDate: upcomingDates[0]?.date ?? null,
    startDate,
    endDate,
    spanMonths,
    perMonthAvg: spanMonths > 0 ? Math.round((dated.length / spanMonths) * 10) / 10 : dated.length,
    yearLabel,
    languages: Array.from(langs) as NonNullable<SessionProgramSlot['language']>[],
    timezone: slots.find((s) => s.timezone)?.timezone ?? null,
    cities,
    facilitators,
    sessionCodes: slots.map((s) => s.sessionCode).filter(Boolean) as string[],
  };
};

const formatCompactDate = (date: Date, timezone?: string | null) =>
  date.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: timezone || undefined,
  });

const formatRangeLabel = (insights: ProgramInsights, timezone?: string | null) => {
  if (!insights.startDate) return null;
  if (!insights.endDate || insights.count === 1) {
    return formatCompactDate(insights.startDate, timezone);
  }
  const tz = timezone || undefined;
  const start = insights.startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: tz });
  const end = insights.endDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: tz,
  });
  return `${start} → ${end}`;
};

const StatPill: React.FC<{
  label: string;
  value: string | number;
  icon: React.ElementType;
  accent?: 'indigo' | 'emerald' | 'amber' | 'slate';
}> = ({ label, value, icon: Icon, accent = 'indigo' }) => {
  const colors = {
    indigo: 'border-indigo-100 bg-white text-indigo-600',
    emerald: 'border-emerald-100 bg-white text-emerald-600',
    amber: 'border-amber-100 bg-white text-amber-600',
    slate: 'border-slate-200 bg-white text-slate-500',
  };

  return (
    <div className={`inline-flex min-w-[7.5rem] flex-col rounded-2xl border px-4 py-3 shadow-sm ${colors[accent].split(' ').slice(0, 2).join(' ')}`}>
      <div className="mb-1 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${colors[accent].split(' ')[2]}`} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      </div>
      <span className="text-xl font-black tabular-nums text-slate-900">{value}</span>
    </div>
  );
};

export const AdminProgramsPage: React.FC = () => {
  const [programs, setPrograms] = useState<SessionProgram[]>([]);
  const [currentUser, setCurrentUser] = useState<AdminAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<SessionProgram | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const canViewPrograms = canSeeAdminSection(currentUser, 'programs');
  const canCreatePrograms = canPerformAdminAction(currentUser, 'programsCreate');
  const canEditPrograms = canPerformAdminAction(currentUser, 'programsEdit');
  const canDeletePrograms = canPerformAdminAction(currentUser, 'programsDelete');

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const data = await sessionProgramsApi.getAllPrograms();
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await authApi.me();
        setCurrentUser(user);
        if (canSeeAdminSection(user, 'programs')) {
          await loadPrograms();
        }
      } catch (error) {
        console.error('Failed to load programs page:', error);
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, []);

  const programInsights = useMemo(
    () => new Map(programs.map((p) => [p.id, getProgramInsights(p.slots)])),
    [programs]
  );

  const sortedPrograms = useMemo(
    () => [...programs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [programs]
  );

  const filteredPrograms = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedPrograms;
    return sortedPrograms.filter((program) => {
      const haystack = [
        program.name,
        program.description,
        ...program.slots.map((s) => s.sessionCode),
        ...program.slots.map((s) => s.location),
        ...program.slots.map((s) => s.facilitator),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedPrograms, searchQuery]);

  const globalStats = useMemo(() => {
    const allInsights = programs.map((p) => programInsights.get(p.id)!);
    const totalSeances = allInsights.reduce((n, i) => n + i.count, 0);
    const upcoming = allInsights.reduce((n, i) => n + i.upcoming, 0);
    const withSlots = allInsights.filter((i) => i.count > 0).length;
    const maxSeances = Math.max(0, ...allInsights.map((i) => i.count));
    const avg = programs.length > 0 ? Math.round(totalSeances / programs.length) : 0;

    return { totalSeances, upcoming, withSlots, maxSeances, avg };
  }, [programs, programInsights]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await sessionProgramsApi.deleteProgram(deleteTarget.id);
      setDeleteTarget(null);
      await loadPrograms();
    } catch (error) {
      console.error('Failed to delete program:', error);
      alert('Erreur lors de la suppression');
    }
  };

  if (!loading && !canViewPrograms) {
    return (
      <div className="flex min-h-full flex-col font-sans text-slate-900">
        <main className="mx-auto w-full max-w-[1200px] flex-grow px-4 py-16 text-center text-slate-500">
          Accès refusé aux programmes.
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <div className="sticky top-0 z-10 -mr-6 -mt-6 mb-0 border-b border-indigo-200/60 bg-white/90 px-6 py-4 shadow-sm shadow-indigo-500/5 backdrop-blur-sm lg:-mr-8 lg:-mt-8 lg:px-8">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-indigo-600">
              <Sparkles className="h-3 w-3" />
              Sessions en série
            </span>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">Programmes</h1>
          </div>
          {canCreatePrograms && (
            <Link
              to="/admin/programs/new"
              className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition-transform hover:-translate-y-0.5 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" />
              Nouveau programme
            </Link>
          )}
        </div>
      </div>

      <main className="mx-auto w-full max-w-[1200px] flex-grow px-4 py-8 sm:px-6 lg:px-8">
        <p className="mb-6 max-w-2xl text-base text-slate-500">
          Parcours annuels ou trimestriels — un programme peut regrouper des dizaines de dates planifiées sur l'année.
        </p>

        {!loading && programs.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-3">
            <StatPill label="Programmes" value={programs.length} icon={Layers} />
            <StatPill label="Séances planifiées" value={globalStats.totalSeances} icon={CalendarRange} />
            <StatPill label="À venir" value={globalStats.upcoming} icon={CalendarClock} accent="emerald" />
            <StatPill
              label="Moy. / programme"
              value={globalStats.avg || '—'}
              icon={TrendingUp}
              accent="slate"
            />
            {globalStats.maxSeances > 0 && (
              <StatPill label="Plus long parcours" value={globalStats.maxSeances} icon={Layers} accent="amber" />
            )}
          </div>
        )}

        {!loading && programs.length > 0 && (
          <div className="relative mb-5 max-w-md">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un programme, un code, une ville, un animateur…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
            />
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-slate-500">Chargement...</div>
        ) : programs.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-indigo-200 bg-white px-8 py-16 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
              <Layers className="h-7 w-7" />
            </div>
            <p className="text-lg font-bold text-slate-800">Aucun programme pour l'instant</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
              Planifiez 40, 50 dates ou plus sur l'année — chaque séance devient une session avec son code.
            </p>
            {canCreatePrograms && (
              <Link
                to="/admin/programs/new"
                className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                Créer un programme
              </Link>
            )}
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500">
            Aucun programme ne correspond à « {searchQuery} ».
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPrograms.map((program) => {
              const insights = programInsights.get(program.id)!;
              const isLeadlab = program.gameMode === 'leadlab';
              const rangeLabel = formatRangeLabel(insights, insights.timezone);
              const previewCodes = insights.sessionCodes.slice(0, 2);
              const hiddenCodes = insights.sessionCodes.length - previewCodes.length;

              return (
                <article
                  key={program.id}
                  className="group relative overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm transition-shadow hover:shadow-md hover:shadow-indigo-500/5"
                >
                  <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-500 to-violet-500" />

                  <div className="flex flex-col gap-4 p-5 pl-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-bold text-slate-900 transition-colors group-hover:text-indigo-700">
                          {program.name}
                        </h2>
                        <span
                          className={`rounded-lg px-2 py-0.5 text-[11px] font-bold ${
                            isLeadlab ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                          }`}
                        >
                          {isLeadlab ? 'Lead Lab' : 'Team Lab'}
                        </span>
                      </div>

                      {insights.count === 0 ? (
                        <p className="mt-2 text-sm font-medium text-amber-600">Aucune séance planifiée</p>
                      ) : (
                        <>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="inline-flex items-center rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-black text-white">
                              {insights.count} séance{insights.count !== 1 ? 's' : ''}
                            </span>
                            {insights.yearLabel && (
                              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                {insights.yearLabel}
                              </span>
                            )}
                            {insights.spanMonths > 1 && (
                              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                {insights.spanMonths} mois
                              </span>
                            )}
                            {insights.perMonthAvg > 0 && insights.spanMonths > 1 && (
                              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                ~{insights.perMonthAvg} / mois
                              </span>
                            )}
                            {insights.upcoming > 0 && (
                              <span className="inline-flex items-center rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                {insights.upcoming} à venir
                              </span>
                            )}
                            {insights.past > 0 && (
                              <span className="inline-flex items-center rounded-lg bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500">
                                {insights.past} passée{insights.past !== 1 ? 's' : ''}
                              </span>
                            )}
                            {insights.cities.length > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                <MapPin className="h-3 w-3 text-slate-400" />
                                {insights.cities[0]}
                                {insights.cities.length > 1 && ` +${insights.cities.length - 1}`}
                              </span>
                            )}
                            {insights.facilitators.length > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                <UserRound className="h-3 w-3 text-slate-400" />
                                {insights.facilitators[0]}
                                {insights.facilitators.length > 1 && ` +${insights.facilitators.length - 1}`}
                              </span>
                            )}
                            {insights.timezone && insights.cities.length === 0 && (
                              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                {timezoneShortLabel(insights.timezone)}
                              </span>
                            )}
                            {insights.languages.map((lang) => {
                              const opt = SESSION_LANGUAGE_OPTIONS.find((o) => o.value === lang);
                              return opt ? (
                                <span
                                  key={lang}
                                  className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600"
                                >
                                  <LanguageFlag countryCode={opt.countryCode} title={opt.label} className="h-3 w-4 rounded-sm" />
                                  {lang.toUpperCase()}
                                </span>
                              ) : null;
                            })}
                          </div>

                          {rangeLabel && (
                            <p className="mt-2 text-sm text-slate-500">
                              <span className="font-semibold text-slate-700">{rangeLabel}</span>
                              {insights.nextDate && (
                                <span className="ml-2 text-emerald-700">
                                  · Prochaine : {formatCompactDate(insights.nextDate, insights.timezone)}
                                </span>
                              )}
                            </p>
                          )}

                          {previewCodes.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Codes</span>
                              {previewCodes.map((code) => (
                                <span
                                  key={code}
                                  className="rounded-md bg-indigo-50 px-2 py-0.5 font-mono text-[11px] font-bold text-indigo-600"
                                >
                                  {code}
                                </span>
                              ))}
                              {hiddenCodes > 0 && (
                                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
                                  +{hiddenCodes}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2 lg:flex-col lg:items-stretch">
                      {canEditPrograms && (
                        <Link
                          to={`/admin/programs/${program.id}/edit`}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-bold text-slate-700 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Modifier
                        </Link>
                      )}
                      {canDeletePrograms && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(program)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-100 bg-red-50/50 px-3.5 py-2 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-[100]">
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
              <h3 className="text-xl font-black text-slate-900">Supprimer « {deleteTarget.name} » ?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Le programme sera supprimé. Les {deleteTarget.slots.length} session
                {deleteTarget.slots.length !== 1 ? 's' : ''} créée{deleteTarget.slots.length !== 1 ? 's' : ''} restent
                dans la liste Sessions, détachées du programme.
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 rounded-2xl bg-slate-100 py-3 font-bold text-slate-700"
                >
                  Annuler
                </button>
                <button onClick={() => void handleDelete()} className="flex-1 rounded-2xl bg-red-600 py-3 font-black text-white">
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
